import { Container, LogManager } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { UniversalEntitySelect } from '../../../dialog/universal-entity-select';
import { createMedia } from '../../utils/file';
import { extractMediaDescription } from '../blocks/bootstrap-components/components/imageCaption';
import openEditorModal from './editorModal';
import './mediaPickerModal.less';

const log = LogManager.getLogger('MediaPickerModal');

const ACCEPT_BY_TYPE = {
    image: 'image/*',
    video: 'video/*',
    audio: 'audio/*',
};

const ICON_UPLOAD = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>`;
const ICON_LIBRARY = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>`;
const ICON_DROP = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v14"/><polyline points="6 10 12 16 18 10"/><path d="M5 20h14"/></svg>`;

function getMediaSrc(item) {
    if (!item?.file) return '';
    return item.file.previewUrl
        ? item.file.previewUrl.replace('$width', '1500').replace('$height', '-')
        : item.file.downloadUrl || '';
}

function normalizeUploadedMedia(data) {
    const url = data?.file?.previewUrl
        ? data.file.previewUrl.replace('$width', '1500').replace('$height', '-')
        : data?.file?.downloadUrl || data?.source_url || '';
    return {
        id: data?.id,
        url,
        src: url,
        title: data?.title || '',
        alt: data?.alt || '',
        description: extractMediaDescription(data),
        type: data?.media_type || data?.type || 'image',
        file: data?.file,
        raw: data,
    };
}

function normalizeLibraryMedia(item) {
    const url = getMediaSrc(item);
    return {
        id: item?.id,
        url,
        src: url,
        title: item?.title || '',
        alt: item?.alt || '',
        description: extractMediaDescription(item),
        type: item?.type || 'image',
        file: item?.file,
        raw: item,
    };
}

/**
 * Open a media picker modal that lets the user upload a file, drag &amp; drop
 * a file, or select from the existing media database.
 *
 * @param {Object} [options]
 * @param {string[]} [options.allowedTypes=['image']]
 * @param {string} [options.title='Medien auswählen']
 * @returns {Promise<null|{id:any,url:string,src:string,title:string,alt:string,description:string,type:string,file:any,raw:any}>}
 */
export const openMediaPickerModal = (options = {}) => {
    const { allowedTypes = ['image'], title = 'Medien auswählen' } = options;

    const accept = allowedTypes
        .map((t) => ACCEPT_BY_TYPE[t] || `${t}/*`)
        .join(',');

    return new Promise((resolve) => {
        let resolved = false;
        let awaitingExternal = false;
        const finish = (result) => {
            if (resolved) return;
            resolved = true;
            resolve(result);
        };

        const body = document.createElement('div');
        body.className = 'sio-media-picker';

        body.innerHTML = `
            <div class="sio-media-picker__options">
                <button type="button" class="sio-media-picker__option" data-action="upload">
                    <span class="sio-media-picker__icon">${ICON_UPLOAD}</span>
                    <span class="sio-media-picker__title">Hochladen</span>
                    <span class="sio-media-picker__hint">Datei vom Computer wählen</span>
                </button>
                <button type="button" class="sio-media-picker__option" data-action="library">
                    <span class="sio-media-picker__icon">${ICON_LIBRARY}</span>
                    <span class="sio-media-picker__title">Medien auswählen</span>
                    <span class="sio-media-picker__hint">Aus der Mediathek</span>
                </button>
            </div>

            <div class="sio-media-picker__dropzone" data-action="dropzone">
                <span class="sio-media-picker__icon sio-media-picker__icon--lg">${ICON_DROP}</span>
                <span class="sio-media-picker__drop-title">Oder Datei hier ablegen</span>
                <span class="sio-media-picker__hint">Drag &amp; Drop unterstützt</span>
            </div>

            <div class="sio-media-picker__status" hidden>
                <div class="sio-media-picker__spinner"></div>
                <span class="sio-media-picker__status-text">Wird hochgeladen…</span>
            </div>

            <div class="sio-media-picker__error" hidden></div>

            <input type="file" class="sio-media-picker__file-input" accept="${accept}" hidden />
        `;

        const uploadBtn = body.querySelector('[data-action="upload"]');
        const libraryBtn = body.querySelector('[data-action="library"]');
        const dropzone = body.querySelector('[data-action="dropzone"]');
        const fileInput = body.querySelector('.sio-media-picker__file-input');
        const statusEl = body.querySelector('.sio-media-picker__status');
        const statusText = body.querySelector('.sio-media-picker__status-text');
        const errorEl = body.querySelector('.sio-media-picker__error');

        let modalCloseRef = null;

        const setBusy = (busy, text = 'Wird hochgeladen…') => {
            statusText.textContent = text;
            statusEl.hidden = !busy;
            uploadBtn.disabled = busy;
            libraryBtn.disabled = busy;
            dropzone.classList.toggle(
                'sio-media-picker__dropzone--disabled',
                busy
            );
        };

        const showError = (message) => {
            errorEl.textContent = message;
            errorEl.hidden = false;
        };

        const clearError = () => {
            errorEl.hidden = true;
            errorEl.textContent = '';
        };

        const isAllowed = (file) => {
            if (!file) return false;
            const major = (file.type || '').split('/')[0];
            return allowedTypes.includes(major);
        };

        const handleFile = async (file) => {
            clearError();
            if (!isAllowed(file)) {
                showError(
                    `Dateityp nicht erlaubt. Erlaubt: ${allowedTypes.join(', ')}`
                );
                return;
            }
            try {
                setBusy(true);
                const uploaded = await createMedia(file, file.name);
                const result = normalizeUploadedMedia(uploaded);
                finish(result);
                modalCloseRef?.();
            } catch (err) {
                log.error('Upload failed', err);
                setBusy(false);
                showError(
                    err?.message ||
                        'Upload fehlgeschlagen. Bitte erneut versuchen.'
                );
            }
        };

        uploadBtn.addEventListener('click', () => {
            clearError();
            fileInput.click();
        });

        fileInput.addEventListener('change', (event) => {
            const file = event.target.files?.[0];
            if (file) handleFile(file);
            fileInput.value = '';
        });

        libraryBtn.addEventListener('click', () => {
            clearError();
            awaitingExternal = true;
            modalCloseRef?.();
            const dialogService = Container.instance.get(DialogService);
            dialogService
                .open({
                    viewModel: UniversalEntitySelect,
                    model: {
                        selectModelId: 'media-database/media-item',
                        conditions: { type: { $in: allowedTypes } },
                        multiSelect: false,
                    },
                })
                .whenClosed((data) => {
                    const items = data.output;
                    if (!items || items.length === 0) {
                        finish(null);
                        return;
                    }
                    const result = normalizeLibraryMedia(items[0]);
                    finish(result.url ? result : null);
                });
        });

        ['dragenter', 'dragover'].forEach((evt) => {
            dropzone.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropzone.classList.add('sio-media-picker__dropzone--active');
            });
        });
        ['dragleave', 'drop'].forEach((evt) => {
            dropzone.addEventListener(evt, (e) => {
                e.preventDefault();
                e.stopPropagation();
                dropzone.classList.remove('sio-media-picker__dropzone--active');
            });
        });
        dropzone.addEventListener('drop', (e) => {
            const file = e.dataTransfer?.files?.[0];
            if (file) handleFile(file);
        });

        openEditorModal({
            title,
            content: body,
            width: 560,
            showConfirm: false,
            showCancel: true,
            cancelText: 'Abbrechen',
            onReady: (close) => {
                modalCloseRef = () =>
                    close({
                        confirmed: true,
                        action: 'programmatic',
                        value: null,
                    });
            },
        }).then(() => {
            if (!resolved && !awaitingExternal) finish(null);
        });
    });
};

export default openMediaPickerModal;
